import { Modal } from 'antd'

import SSNDisplay from 'components/SSNDisplay'
import dayjs from 'dayjs'
import { InvestingPersonEnum, RetirementAccountEnum } from 'interfaces'
import React, { useEffect, useState } from 'react'
import { isDesktop, isMobile, isTablet } from 'react-device-detect'
import { getOnboardingType } from 'utils'

import Button from 'components/Button'
import LoaderModal from '../LoaderModal'
import Company from './Company'
import IRA from './IRA'
import Joint from './Joint'
import Trust from './Trust'

interface ProceedModalProps {
  isVisible: boolean
  onProceed: () => void
  onCancel: () => void
  data: () => any
}

export default function DataConfirmationModal({ isVisible, onProceed, onCancel, data }: ProceedModalProps) {
  const state = data()
  const [isLoading, setIsLoading] = useState(false)
  const [modalStyle, setModalStyle] = useState({})

  useEffect(() => {
    const updateModalStyle = () => {
      if (!isDesktop) {
        setModalStyle({
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          margin: 0,
          maxWidth: '100%',
          width: '100%',
          height: '100%',
        })
      } else {
        setModalStyle({
          width: '60vw',
        })
      }
    }

    updateModalStyle()
    window.addEventListener('resize', updateModalStyle)

    return () => {
      window.removeEventListener('resize', updateModalStyle)
    }
  }, [])

  return (
    <Modal
      width={`${(isMobile || isTablet) && !isDesktop ? '100vw' : '60vw'}`}
      open={isVisible}
      centered
      footer={null}
      closable={true}
      onCancel={onCancel}
      className="font-inter"
      style={{
        top: (isMobile || isTablet) && !isDesktop ? 0 : undefined,
        maxWidth: (isMobile || isTablet) && !isDesktop ? '100%' : undefined,
        margin: (isMobile || isTablet) && !isDesktop ? 0 : undefined,
        padding: (isMobile || isTablet) && !isDesktop ? 0 : undefined,
      }}
    >
      <div className="p-4">
        <div className="flex h-full flex-col">
          <div className="flex-grow overflow-y-auto px-4 py-6">
            <div className="mb-6 flex gap-3 rounded-md bg-[#FFE5D3] p-4">
              <div className="text-sm">
                Before proceeding to Payments, please take a moment to check the details below. If you need to make any
                updates, just go back to the previous pages.
              </div>
            </div>
            <div className="mb-6 text-left text-2xl font-semibold">
              {state.investingProfile.option === InvestingPersonEnum.FOR_MYSELF && 'Investor Details'}
              {state.investingProfile.option === InvestingPersonEnum.JOINTLY && 'Primary Investor Details'}
              {state.investingProfile.option === InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY &&
                'Authorized Person Details'}
              {state?.investingProfile.option === InvestingPersonEnum.RETIREMENT &&
                state?.investingProfile.retirementAccountType === RetirementAccountEnum.RETIREMENT_ACCOUNT &&
                'Retirement Account Holder Details'}
              {state?.investingProfile.option === InvestingPersonEnum.RETIREMENT &&
                state?.investingProfile.retirementAccountType === RetirementAccountEnum.TRUST_ACCOUNT &&
                'Authorized Person Details'}
            </div>

            <div className="flex flex-col md:flex-row md:gap-6">
              <div className="mb-6 w-full md:mb-0 md:w-1/2">
                <h3 className="mb-2 text-lg font-medium uppercase">Basic Information</h3>
                <div className="space-y-2 pl-2">
                  <div>
                    <span className="font-medium uppercase">Investor type:</span>{' '}
                    {getOnboardingType(state.investingProfile.option)}
                  </div>
                  <div>
                    <span className="font-medium uppercase">Email:</span> {state.email}
                  </div>
                  <div>
                    <span className="font-medium uppercase">First Name:</span> {state.basicInfo.firstName}
                  </div>
                  <div>
                    <span className="font-medium uppercase">Middle Name:</span> {state.basicInfo.middleName}
                  </div>
                  <div>
                    <span className="font-medium uppercase">Last Name:</span> {state.basicInfo.lastName}
                  </div>
                  <div>
                    <span className="font-medium uppercase">Suffix:</span> {state.basicInfo.suffix}
                  </div>
                </div>
                <h3 className="mb-2 mt-6 text-lg font-medium uppercase">Address Information</h3>
                <div className="space-y-2 pl-2">
                  <div>
                    <span className="font-medium uppercase">Country:</span> {state.addressInfo.country}
                  </div>
                  <div>
                    <span className="font-medium uppercase">State:</span> {state.addressInfo.state}
                  </div>
                  <div>
                    <span className="font-medium uppercase">Street Line 1:</span> {state.addressInfo.Street1}
                  </div>
                  <div>
                    <span className="font-medium uppercase">Street Line 2:</span> {state.addressInfo.Street2}
                  </div>
                  <div>
                    <span className="font-medium uppercase">City:</span> {state.addressInfo.city}
                  </div>
                  <div>
                    <span className="font-medium uppercase">Postal Code:</span> {state.addressInfo.postalCode}
                  </div>
                </div>
              </div>
              <div className="w-full md:w-1/2">
                <h3 className="mb-2 text-lg font-medium uppercase">Personal Information</h3>
                <div className="space-y-2 pl-2">
                  <div>
                    <span className="font-medium uppercase">Phone Number:</span> {state.personalInfo.phoneNumber}
                  </div>
                  <div>
                    <span className="font-medium uppercase">Date of Birth:</span>{' '}
                    {String(dayjs(state.personalInfo.birthday).format('MM/DD/YYYY'))}
                  </div>
                </div>
                <h3 className="mb-2 mt-6 text-lg font-medium uppercase">Financial Information</h3>
                <SSNDisplay ssn={state.financialInfo.ssn} />
              </div>
            </div>

            {state.investingProfile.option === InvestingPersonEnum.JOINTLY && <Joint data={data} />}
            {state.investingProfile.option === InvestingPersonEnum.IN_BEHALF_OF_AN_ENTITY && <Company data={data} />}
            {state.investingProfile.option === InvestingPersonEnum.RETIREMENT &&
              state?.investingProfile.retirementAccountType === RetirementAccountEnum.TRUST_ACCOUNT && (
                <Trust data={data} />
              )}
            {state.investingProfile.option === InvestingPersonEnum.RETIREMENT &&
              state?.investingProfile.retirementAccountType === RetirementAccountEnum.RETIREMENT_ACCOUNT && (
                <IRA data={data} />
              )}
          </div>

          <div className="w-full justify-center gap-3 py-10 sm:block md:flex">
            <div className="flex flex-col sm:w-[100%] md:w-[20%]">
              <Button btnName={'Close'} onClickHandler={onCancel} />
            </div>
            <div className="flex flex-col sm:w-[100%] md:w-[80%]">
              <Button
                btnName={'Proceed'}
                btnType={'button'}
                onClickHandler={() => {
                  setIsLoading(true)
                  onProceed()
                }}
              />
            </div>
          </div>
          <LoaderModal isVisible={isLoading} />
        </div>
      </div>
    </Modal>
  )
}
